// Client-side derivations for the growth-panel chart tabs. All four views are
// built from the per-account performance we already receive ({date, value,
// netFlow}) so there are no extra API calls and multi-account selection works.
//
//   Growth   — chained time-weighted return index (%), flows removed
//   Equity   — raw $ NAV (as charted today)
//   Profit   — cumulative $ P&L net of external flows
//   Drawdown — underwater % of the TWR index
//
// Growth's final value matches applyPortfolioMetrics().totalReturn and the Gain
// tile; the Drawdown trough matches the Max Drawdown metric. That equality is
// only true because all of them now share chainReturns().

import { chainReturns } from './clientReportingReturnChain';
import { firstFundedIndex, materialityFloor, trimToFunded } from './clientReportingInception';

/** Carry the last known balance across calendar days with no broker update (NT/Alpaca). */
function forwardFillCalendarDays(rawPoints) {
  const sorted = [...(rawPoints || [])].sort((a, b) => a.date.localeCompare(b.date));
  if (sorted.length < 2) return sorted;
  const out = [sorted[0]];
  for (let i = 1; i < sorted.length; i += 1) {
    const prev = sorted[i - 1];
    const cur = sorted[i];
    let d = new Date(`${prev.date}T12:00:00Z`);
    const end = new Date(`${cur.date}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() + 1);
    while (d < end) {
      out.push({
        date: d.toISOString().slice(0, 10),
        value: prev.value,
        netFlow: 0,
      });
      d.setUTCDate(d.getUTCDate() + 1);
    }
    out.push(cur);
  }
  return out;
}

export const CHART_TABS = [
  { id: 'equity', label: 'Equity', valueFormat: 'currency' },
  { id: 'growth', label: 'Growth', valueFormat: 'percent' },
  { id: 'profit', label: 'Profit', valueFormat: 'currency' },
  { id: 'drawdown', label: 'Drawdown', valueFormat: 'percent' },
];

export function tabValueFormat(tabId) {
  return CHART_TABS.find((t) => t.id === tabId)?.valueFormat || 'currency';
}

// points: [{ date, value, netFlow }] sorted ascending by date.
function transformSeries(rawPoints, tab) {
  // Equity is raw NAV and must show the funding step — it is the account's
  // actual value, not a performance measure. After funding, junk broker rows
  // (0.00 or a few cents on days with no valuation) are shown as a flat carry
  // so the chart does not sawtooth to $0 and break benchmark overlays.
  if (tab === 'equity') {
    const points = forwardFillCalendarDays(rawPoints);
    const out = [];
    const fundedFrom = firstFundedIndex(points);
    const funded = trimToFunded(points);
    const measurePoints = funded.length >= 2 ? funded : points;
    const floor = materialityFloor(measurePoints);
    let lastMeasurable = -1;
    for (let i = measurePoints.length - 1; i >= 0; i -= 1) {
      if ((Number(measurePoints[i].value) || 0) >= floor) {
        lastMeasurable = i;
        break;
      }
    }
    const dateToMeasureIdx = new Map(measurePoints.map((point, index) => [point.date, index]));
    let lastDisplay = null;

    points.forEach((point, index) => {
      const value = Number(point.value) || 0;
      if (index < fundedFrom) {
        out.push([point.date, value]);
        return;
      }
      const measureIdx = dateToMeasureIdx.get(point.date);
      if (measureIdx == null) {
        lastDisplay = value;
        out.push([point.date, value]);
        return;
      }
      const isWipeoutTail = measureIdx > lastMeasurable;
      const isGap = !isWipeoutTail && value < floor;
      if (isGap) {
        out.push([point.date, lastDisplay ?? value]);
        return;
      }
      lastDisplay = value;
      out.push([point.date, value]);
    });
    return out;
  }

  const out = [];
  // The other three measure performance, so they start where the money did, and
  // share one chain with the tiles — see clientReportingReturnChain.
  const { points: chained } = chainReturns(rawPoints);

  for (const p of chained) {
    if (tab === 'growth') out.push([p.date, p.growthPct]);
    else if (tab === 'profit') out.push([p.date, p.cumProfit]);
    else out.push([p.date, p.drawdownPct]);
  }

  return out;
}

export function buildTabChartData(seriesRaw, seriesNames, tab) {
  const dateMap = new Map();
  for (const name of seriesNames || []) {
    const points = seriesRaw?.[name];
    if (!points || !points.length) continue;
    for (const [date, value] of transformSeries(points, tab)) {
      const existing = dateMap.get(date) || { date };
      existing[name] = value;
      dateMap.set(date, existing);
    }
  }
  return Array.from(dateMap.values()).sort((a, b) => a.date.localeCompare(b.date));
}
